import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import React, { useState } from 'react'
import { type ISlot } from '../../../models/DistributionPlan'
import theme from '../../../theme'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import GenericModal from '../../../components/GenericModal'
import { DistributionManager } from '../../../APIManager'
import { notification } from '../../../components/Notifications'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import CustomTooltip from '../../../components/CustomTooltip'
import { deleteSlotBySlotId } from '../../../APIManager/DistributionManager'

interface DistributionPlansTableProps {
  slotsData: ISlot[] | undefined
  handleDeleteSlot?: (slotId: string) => void
  slotsLoading?: boolean
}

export const DistributionPlansTable: React.FC<DistributionPlansTableProps> = ({ slotsData, handleDeleteSlot, slotsLoading }): JSX.Element => {
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false)
  const [selectedSlot, setSelectedSlot] = useState<ISlot | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleConfirmDelete = async (slotId: string): Promise<void> => {
    setIsLoading(true)
    try {
      await deleteSlotBySlotId(slotId)
      notification('Deleted successfully', { variant: 'success' })
      handleDeleteSlot?.(slotId)
    } catch (error) {
      console.error('Error deleting slot:', error)
      notification('Failed to delete Survey Project instance', { variant: 'error' })
    } finally {
      setConfirmModalOpen(false)
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{ display: 'flex', width: '100%', overflow: 'hidden' }}
    >
      <TableContainer sx= {{ px: theme.spacing(6), overflowY: 'hidden', width: '100%' }}>
        {slotsLoading &&
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: theme.spacing(4) }}>
            <CircularProgress />
          </Box>
        }
        {!slotsLoading &&
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                  <TableCell sx={{ textAlign: 'flex-start', fontWeight: 'bold' }}>Term</TableCell>
                  <TableCell sx={{ textAlign: 'flex-start', fontWeight: 'bold' }}>Survey Template</TableCell>
                  <TableCell sx={{ textAlign: 'flex-start', fontWeight: 'bold' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx= {{ justifyContent: 'center', alignItems: 'center' }}>
              {slotsData?.map((slot: ISlot) => (
                <TableRow key={slot.slotId}>
                  <TableCell sx={{ textAlign: 'flex-start', borderBottom: 'none' }}>{slot.term?.termName ?? 'N/A'}</TableCell>
                  <TableCell sx={{ textAlign: 'flex-start', borderBottom: 'none' }}>{slot.surveyTemplate?.title ?? 'N/A'}</TableCell>
                  <TableCell sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', borderBottom: 'none' }}>
                    <Box
                      sx={{
                        '&:hover': { opacity: 0.7 },
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setSelectedSlot(slot)
                        setConfirmModalOpen(true)
                      }}
                    >
                      <CustomTooltip title='Delete'>
                        <DeleteOutlineOutlinedIcon color='error' />
                      </CustomTooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        }
      </TableContainer>
      <GenericModal
        isOpen={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false) }}
        title={`Delete "${selectedSlot?.title ?? 'Survey Project instance'}"`}
        maxWidth='sm'
        confirmActionText='Confirm'
        confirmActionType={ButtonType.Destructive}
        destructiveButtonText='Cancel'
        confirmButtonLoading={isLoading}
        onConfirm={async () => {
          if (!selectedSlot) {
            notification('No Survey Project instance selected for deletion', { variant: 'error' })
            return
          }
          await handleConfirmDelete(selectedSlot.slotId)
        }}
      >
        Are you sure you want to delete the Survey Project instance?
      </GenericModal>
    </Box>
  )
}
